/**
 * Pantalla de acceso denegado (production).
 * Persona clinical → CTA login; portal / desconocido → sin login profesional.
 */
import React from 'react'
import { useTranslation } from 'react-i18next'
import { ACCESS_REASONS, UDV_PERSONAS } from '../plan/constants'

/**
 * @param {{
 *   reason?: string,
 *   persona?: string|null,
 *   onLogin?: (() => void)|null,
 *   onOpenLocalStudies?: (() => void)|null,
 *   onInstallPwa?: (() => void)|null,
 * }} props
 */
function AccessDeniedSplash({
  reason,
  persona = null,
  onLogin = null,
  onOpenLocalStudies = null,
  onInstallPwa = null,
}) {
  const { t } = useTranslation()
  let detailKey = 'access.missingEntitlements'
  if (reason === ACCESS_REASONS.MISSING_TOKEN) detailKey = 'access.missingToken'
  if (reason === ACCESS_REASONS.MISSING_ENTITLEMENTS) {
    detailKey = 'access.missingEntitlements'
  }
  if (reason === ACCESS_REASONS.MISSING_CONTROLLER_PLAN) {
    detailKey = 'access.missingControllerPlan'
  }
  if (reason === ACCESS_REASONS.INVALID_TOKEN) detailKey = 'access.invalidToken'
  if (reason === ACCESS_REASONS.DISABLED_TOKEN) detailKey = 'access.disabledToken'
  if (reason === ACCESS_REASONS.INVALID_TICKET) detailKey = 'access.invalidTicket'
  if (reason === ACCESS_REASONS.INVALID_WORKLIST) detailKey = 'access.invalidWorklist'
  if (reason === ACCESS_REASONS.TICKET_PENDING) detailKey = 'access.ticketPending'
  if (reason === ACCESS_REASONS.INVALID_INVITE) detailKey = 'access.invalidInvite'
  if (reason === ACCESS_REASONS.INVITE_LOGIN_REQUIRED) {
    detailKey = 'access.inviteLoginRequired'
  }
  if (reason === ACCESS_REASONS.WORKSPACE_EMBED_REQUIRED) {
    detailKey = 'access.workspaceEmbedRequired'
  }

  const isPortal = persona === UDV_PERSONAS.PORTAL
  const isClinical = persona === UDV_PERSONAS.CLINICAL
  const showLogin = isClinical && typeof onLogin === 'function'
  const showInstall = typeof onInstallPwa === 'function'

  // Motivos específicos del ticket/invite: no tapar con el copy genérico de portal.
  const preferSpecificDetail =
    reason === ACCESS_REASONS.INVALID_TICKET ||
    reason === ACCESS_REASONS.INVALID_WORKLIST ||
    reason === ACCESS_REASONS.TICKET_PENDING ||
    reason === ACCESS_REASONS.INVALID_INVITE ||
    reason === ACCESS_REASONS.INVITE_LOGIN_REQUIRED ||
    reason === ACCESS_REASONS.WORKSPACE_EMBED_REQUIRED ||
    reason === ACCESS_REASONS.INVALID_TOKEN ||
    reason === ACCESS_REASONS.DISABLED_TOKEN ||
    reason === ACCESS_REASONS.MISSING_CONTROLLER_PLAN

  const detailText =
    isPortal && !preferSpecificDetail ? t('access.portalOnly') : t(detailKey)

  const btnBase = {
    display: 'inline-block',
    marginTop: 16,
    marginRight: 8,
    padding: '10px 18px',
    borderRadius: 6,
    border: 'none',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        background: 'linear-gradient(160deg, #0f1419 0%, #1a2332 55%, #121820 100%)',
        color: '#e8eef6',
        fontFamily: 'system-ui, sans-serif',
        textAlign: 'center',
        boxSizing: 'border-box',
      }}
      role="alert"
    >
      <div style={{ maxWidth: 420 }}>
        <div
          style={{
            fontSize: 13,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            opacity: 0.65,
            marginBottom: 12,
          }}
        >
          TJS-UDV
        </div>
        <h1 style={{ fontSize: 22, fontWeight: 600, margin: '0 0 12px' }}>
          {t('access.title')}
        </h1>
        <p style={{ fontSize: 15, lineHeight: 1.5, opacity: 0.85, margin: 0 }}>
          {detailText}
        </p>
        <p
          style={{
            fontSize: 13,
            lineHeight: 1.45,
            opacity: 0.55,
            marginTop: 20,
          }}
        >
          {isPortal
            ? t('access.portalHint')
            : isClinical
              ? t('access.hint')
              : t('access.portalPathHint')}
        </p>

        {showLogin ? (
          <button
            type="button"
            style={{
              ...btnBase,
              background: '#2f6fed',
              color: '#fff',
            }}
            onClick={onLogin}
          >
            {t('access.loginClinical')}
          </button>
        ) : null}

        {typeof onOpenLocalStudies === 'function' ? (
          <button
            type="button"
            style={{
              ...btnBase,
              background: 'transparent',
              color: '#9ecbff',
              border: '1px solid rgba(158, 203, 255, 0.35)',
            }}
            onClick={onOpenLocalStudies}
          >
            {t('access.openLocalStudies')}
          </button>
        ) : null}

        {showInstall ? (
          <button
            type="button"
            style={{
              ...btnBase,
              background: 'transparent',
              color: '#c4a574',
              border: '1px solid rgba(196, 165, 116, 0.45)',
            }}
            onClick={onInstallPwa}
          >
            {t('pwa.install')}
          </button>
        ) : null}
      </div>
    </div>
  )
}

export default AccessDeniedSplash
